import React, { useState, useEffect } from 'react';
import { featureAPI, cityAPI } from '../services/api';
import { AppFeature, City } from '../types';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Stack,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  Refresh,
  Add,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  Public,
  LocationCity,
  ToggleOn,
  ToggleOff,
  AddCircle,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

const FeatureFlags: React.FC = () => {
  const [features, setFeatures] = useState<AppFeature[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [deletingCode, setDeletingCode] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [filterFeatureName, setFilterFeatureName] = useState<string>('');
  const [showCityOverrideDialog, setShowCityOverrideDialog] = useState<boolean>(false);
  const [selectedFeatureCode, setSelectedFeatureCode] = useState<string>('');
  const [newCityOverride, setNewCityOverride] = useState<{ cityId: string; enabled: boolean }>({
    cityId: '',
    enabled: true,
  });
  
  const [form, setForm] = useState<Partial<AppFeature>>({
    featureName: '',
    code: '',
    globalEnabled: false,
    cityOverrides: {},
    description: '',
  });
  
  const [codeError, setCodeError] = useState<string>('');

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchCities();
    fetchFeatures();
  }, []);

  const fetchCities = async () => {
    try {
      const response = await cityAPI.getAll();
      setCities(response.data);
    } catch (error: any) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchFeatures = async () => {
    setLoading(true);
    try {
      const response = await featureAPI.getAllFeatures();
      setFeatures(response.data);
    } catch (error: any) {
      console.error('Error fetching features:', error);
      enqueueSnackbar('Failed to load features', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const validateCode = (code: string): boolean => {
    if (!code) {
      setCodeError('Code is required');
      return false;
    }
    // Alphanumeric with underscore only
    const codeRegex = /^[a-zA-Z0-9_]+$/;
    if (!codeRegex.test(code)) {
      setCodeError('Code must contain only alphanumeric characters and underscores');
      return false;
    }
    setCodeError('');
    return true;
  };

  const handleOpenModal = (feature?: AppFeature) => {
    if (feature) {
      setIsEditMode(true);
      setForm({
        code: feature.code,
        featureName: feature.featureName,
        globalEnabled: feature.globalEnabled ?? false,
        cityOverrides: feature.cityOverrides || {},
        description: feature.description || '',
      });
      setCodeError('');
    } else {
      setIsEditMode(false);
      setForm({
        featureName: '',
        code: '',
        globalEnabled: false,
        cityOverrides: {},
        description: '',
      });
      setCodeError('');
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditMode(false);
    setForm({
      featureName: '',
      code: '',
      globalEnabled: false,
      cityOverrides: {},
      description: '',
    });
    setCodeError('');
  };

  const handleSave = async () => {
    if (!form.featureName) {
      enqueueSnackbar('Feature name is required', { variant: 'error' });
      return;
    }
    
    if (!validateCode(form.code || '')) {
      return;
    }

    setSaving(true);
    try {
      // Ensure feature name is uppercase before saving
      const featureToSave = {
        ...form,
        featureName: form.featureName.toUpperCase(),
        globalEnabled: form.globalEnabled ?? false,
        cityOverrides: form.cityOverrides || {},
      };
      await featureAPI.saveFeature(featureToSave);
      enqueueSnackbar(`Feature ${isEditMode ? 'updated' : 'created'} successfully`, { variant: 'success' });
      handleCloseModal();
      fetchFeatures();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} feature`;
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleGlobalEnabled = async (code: string, currentEnabled: boolean) => {
    try {
      await featureAPI.setGlobalEnabled(code, !currentEnabled);
      enqueueSnackbar(`Global feature ${!currentEnabled ? 'enabled' : 'disabled'}`, { variant: 'success' });
      fetchFeatures();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to toggle feature';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleOpenCityOverrideDialog = (code: string) => {
    setSelectedFeatureCode(code);
    setNewCityOverride({ cityId: '', enabled: true });
    setShowCityOverrideDialog(true);
  };

  const handleCloseCityOverrideDialog = () => {
    setShowCityOverrideDialog(false);
    setSelectedFeatureCode('');
    setNewCityOverride({ cityId: '', enabled: true });
  };

  const handleAddCityOverride = async () => {
    if (!newCityOverride.cityId) {
      enqueueSnackbar('Please select a city', { variant: 'error' });
      return;
    }

    try {
      await featureAPI.setCityOverride(
        selectedFeatureCode,
        newCityOverride.cityId,
        newCityOverride.enabled
      );
      enqueueSnackbar('City override added successfully', { variant: 'success' });
      handleCloseCityOverrideDialog();
      fetchFeatures();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to add city override';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleRemoveCityOverride = async (code: string, cityId: string) => {
    try {
      await featureAPI.removeCityOverride(code, cityId);
      enqueueSnackbar('City override removed successfully', { variant: 'success' });
      fetchFeatures();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to remove city override';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleDelete = async (code: string) => {
    if (!window.confirm(`Are you sure you want to delete feature "${code}"?`)) {
      return;
    }

    setDeletingCode(code);
    try {
      await featureAPI.deleteFeature(code);
      enqueueSnackbar('Feature deleted successfully', { variant: 'success' });
      fetchFeatures();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete feature';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setDeletingCode(null);
    }
  };

  const filteredFeatures = features.filter(feature => {
    if (filterFeatureName && !feature.featureName.toLowerCase().includes(filterFeatureName.toLowerCase())) {
      return false;
    }
    return true;
  });

  const getCityName = (cityId: string) => {
    const city = cities.find(c => c.id === cityId);
    return city ? city.name : cityId;
  };

  const getEffectiveStatus = (feature: AppFeature, cityId?: string): boolean => {
    if (cityId && feature.cityOverrides && feature.cityOverrides[cityId] !== undefined) {
      return feature.cityOverrides[cityId];
    }
    return feature.globalEnabled ?? false;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Feature Flags Management
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchFeatures}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal()}
          >
            Add Feature Flag
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Each feature has a unique code. You can set a global enabled/disabled status and add city-specific overrides.
        City-specific overrides take precedence over the global setting.
      </Alert>

      {/* Filter */}
      <Box sx={{ mb: 2 }}>
        <TextField
          label="Filter by Feature Name"
          value={filterFeatureName}
          onChange={(e) => setFilterFeatureName(e.target.value)}
          size="small"
          sx={{ minWidth: 300 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredFeatures.length === 0 ? (
        <Alert severity="info">No features found</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Feature Name</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Global Status</TableCell>
                <TableCell>City Overrides</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredFeatures.map((feature) => (
                <TableRow key={feature.code}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {feature.featureName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={feature.code}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        icon={feature.globalEnabled ? <CheckCircle /> : <Cancel />}
                        label={feature.globalEnabled ? 'Enabled' : 'Disabled'}
                        color={feature.globalEnabled ? 'success' : 'default'}
                        size="small"
                      />
                      <Tooltip title={feature.globalEnabled ? 'Disable globally' : 'Enable globally'}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleGlobalEnabled(feature.code, feature.globalEnabled ?? false)}
                          color={feature.globalEnabled ? 'error' : 'success'}
                        >
                          {feature.globalEnabled ? <ToggleOff /> : <ToggleOn />}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {feature.cityOverrides && Object.keys(feature.cityOverrides).length > 0 ? (
                        Object.entries(feature.cityOverrides).map(([cityId, enabled]) => (
                          <Chip
                            key={cityId}
                            icon={enabled ? <CheckCircle /> : <Cancel />}
                            label={`${getCityName(cityId)}: ${enabled ? 'Enabled' : 'Disabled'}`}
                            color={enabled ? 'success' : 'default'}
                            size="small"
                            onDelete={() => handleRemoveCityOverride(feature.code, cityId)}
                          />
                        ))
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          None
                        </Typography>
                      )}
                      <Tooltip title="Add city override">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenCityOverrideDialog(feature.code)}
                          color="primary"
                        >
                          <AddCircle fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal(feature)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(feature.code)}
                          disabled={deletingCode === feature.code}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditMode ? 'Edit Feature Flag' : 'Create Feature Flag'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Feature Name"
                value={form.featureName || ''}
                onChange={(e) => {
                  setForm({ ...form, featureName: e.target.value });
                }}
                onBlur={(e) => {
                  // Convert to uppercase when user finishes typing
                  const upperValue = e.target.value.toUpperCase();
                  setForm({ ...form, featureName: upperValue });
                }}
                required
                disabled={isEditMode}
                helperText="e.g., FARE, NOTIFICATIONS, ROUTE_PLANNING (will be converted to uppercase)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Code"
                value={form.code || ''}
                onChange={(e) => {
                  const value = e.target.value.toLowerCase();
                  setForm({ ...form, code: value });
                  validateCode(value);
                }}
                required
                disabled={isEditMode}
                error={!!codeError}
                helperText={codeError || 'Alphanumeric and underscore only (e.g., fare, notifications, route_planning)'}
                inputProps={{ pattern: '[a-zA-Z0-9_]+' }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Global Settings
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.globalEnabled ?? false}
                    onChange={(e) => setForm({ ...form, globalEnabled: e.target.checked })}
                  />
                }
                label="Global Enabled (applies to all cities unless overridden)"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                City-Specific Overrides
              </Typography>
              <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                City overrides can be added after creating the feature. They take precedence over the global setting.
              </Alert>
              {form.cityOverrides && Object.keys(form.cityOverrides).length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {Object.entries(form.cityOverrides).map(([cityId, enabled]) => (
                    <Chip
                      key={cityId}
                      label={`${getCityName(cityId)}: ${enabled ? 'Enabled' : 'Disabled'}`}
                      color={enabled ? 'success' : 'default'}
                      size="small"
                      onDelete={() => {
                        const newOverrides = { ...form.cityOverrides };
                        delete newOverrides[cityId];
                        setForm({ ...form, cityOverrides: newOverrides });
                      }}
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={form.description || ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                multiline
                rows={3}
                helperText="Optional description of what this feature flag controls"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.featureName || !form.code || !!codeError}
          >
            {saving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add City Override Dialog */}
      <Dialog open={showCityOverrideDialog} onClose={handleCloseCityOverrideDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add City Override</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>City</InputLabel>
                <Select
                  value={newCityOverride.cityId}
                  onChange={(e) => setNewCityOverride({ ...newCityOverride, cityId: e.target.value })}
                  label="City"
                >
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newCityOverride.enabled}
                    onChange={(e) => setNewCityOverride({ ...newCityOverride, enabled: e.target.checked })}
                  />
                }
                label="Enabled for this city"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCityOverrideDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddCityOverride}
            disabled={!newCityOverride.cityId}
          >
            Add Override
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeatureFlags;
